/**
 * Converts a canvas element to a blob with timeout protection
 * @param {HTMLCanvasElement} canvas - The canvas element to convert
 * @param {string} mimeType - The MIME type for the blob (e.g., 'image/png')
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns {Promise<Blob>} A promise that resolves with the blob
 * @throws {Error} If conversion fails or times out
 */
export async function canvasToBlobWithTimeout(canvas, mimeType = 'image/png', timeoutMs = 5000) {
  let timeoutId;
  
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Canvas conversion timeout'));
    }, timeoutMs);
  });

  const blobPromise = new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create image from canvas'));
        } else {
          resolve(blob);
        }
      }, mimeType);
    } catch (err) {
      reject(err);
    }
  });

  return Promise.race([blobPromise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

/**
 * Loads an image with timeout protection
 * @param {string} src - The image source URL
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns {Promise<HTMLImageElement>} A promise that resolves with the loaded image
 * @throws {Error} If loading fails or times out
 */
export async function loadImageWithTimeout(src, timeoutMs = 5000) {
  const img = new Image();
  let timeoutId;
  
  const loadPromise = new Promise((resolve, reject) => {
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    img.src = src;
  });

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Image loading timeout'));
    }, timeoutMs);
  });

  return Promise.race([loadPromise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}
